import React, { useState, useEffect } from "react";
import { AuthContext } from "./authContextInstance";
import { auth, db } from "../config/firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

// Proveedor de autenticación con Firebase
export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);

  // Escuchar cambios en el estado de autenticación de Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Usuario autenticado - obtener rol desde Firestore
        try {
          const userDoc = await getDoc(doc(db, "usuarios", firebaseUser.uid));
          const userData = userDoc.data();

          setUsuario({
            uid: firebaseUser.uid,
            nombre: userData?.nombre || firebaseUser.displayName || "Usuario",
            email: firebaseUser.email,
            rol: userData?.rol || "viewer",
          });
        } catch (error) {
          console.error("Error al obtener datos del usuario:", error);
          // Si hay error, usar datos básicos
          setUsuario({
            uid: firebaseUser.uid,
            nombre: firebaseUser.displayName || "Usuario",
            email: firebaseUser.email,
            rol: "viewer",
          });
        }
      } else {
        // Usuario no autenticado
        setUsuario(null);
      }
      setCargando(false);
    });

    return () => unsubscribe();
  }, []);

  // Función para iniciar sesión con email y contraseña
  const iniciarSesionConEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      return {
        success: false,
        error:
          error.code === "auth/invalid-credential"
            ? "Email o contraseña incorrectos"
            : "Error al iniciar sesión. Intenta nuevamente.",
      };
    }
  };

  // Función para registrar nuevo usuario
  const registrarUsuario = async (email, password, nombre) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Guardar información adicional en Firestore
      await setDoc(doc(db, "usuarios", user.uid), {
        nombre: nombre,
        email: email,
        rol: "viewer", // Los usuarios nuevos son viewers por defecto
        fechaCreacion: new Date().toISOString(),
      });

      return { success: true, user };
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      return {
        success: false,
        error:
          error.code === "auth/email-already-in-use"
            ? "Este email ya está registrado"
            : "Error al crear la cuenta. Intenta nuevamente.",
      };
    }
  };

  // Función para cerrar sesión
  const cerrarSesion = async () => {
    try {
      await signOut(auth);
      setUsuario(null);
      window.location.href = "/";
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Función legacy para mantener compatibilidad (usada en LoginAdmin)
  const iniciarSesion = async (emailOrUsername, role = "viewer") => {
    // Esta función se mantiene para compatibilidad con el sistema antiguo
    // pero ahora solo actualiza el estado local sin autenticación real
    console.warn("iniciarSesion legacy - considera usar iniciarSesionConEmail");
    setUsuario({
      nombre: emailOrUsername,
      email: emailOrUsername.includes("@") ? emailOrUsername : "",
      rol: role,
    });
  };

  // Función para verificar si el usuario tiene un rol específico
  const tieneRol = (rolesPermitidos) => {
    if (!usuario) return false;
    if (Array.isArray(rolesPermitidos)) {
      return rolesPermitidos.includes(usuario.rol);
    }
    return usuario.rol === rolesPermitidos;
  };

  // Función para verificar si puede editar
  const puedeEditar = () => {
    return tieneRol(["admin", "editor"]);
  };

  const value = {
    usuario,
    cargando,
    iniciarSesion, // Legacy
    iniciarSesionConEmail,
    registrarUsuario,
    cerrarSesion,
    isAuthenticated: !!usuario,
    tieneRol,
    puedeEditar,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
